import type {
  UkTaxSettingsDTO,
  UkTaxSummary,
  UkSaEstimate,
  UkTaxBoxes,
  VatReturnDraft,
  MtdStatus,
  Ct600Summary,
  CisSubcontractor,
  CisSubcontractorCreateInput,
  Cis300Draft,
  UkTaxDeadline,
  TaxFilingDraftSummary,
} from '@eriko/shared-types';
import { apiClient } from '../lib/api';
import { ENDPOINTS } from './endpoints';

// Settings
export async function getUkTaxSettings(): Promise<UkTaxSettingsDTO> {
  const res = await apiClient.get<UkTaxSettingsDTO>(ENDPOINTS.taxes.settings);
  return res.data;
}

export async function updateUkTaxSettings(input: Partial<UkTaxSettingsDTO>): Promise<UkTaxSettingsDTO> {
  const res = await apiClient.patch<UkTaxSettingsDTO>(ENDPOINTS.taxes.settings, input);
  return res.data;
}

// Summary + Estimate
export async function getUkTaxSummary(taxYear?: string): Promise<UkTaxSummary> {
  const res = await apiClient.get<UkTaxSummary>(ENDPOINTS.taxes.summary, {
    params: taxYear ? { taxYear } : undefined,
  });
  return res.data;
}

export async function getUkSaEstimate(taxYear?: string): Promise<UkSaEstimate> {
  const res = await apiClient.get<UkSaEstimate>(ENDPOINTS.taxes.saEstimate, {
    params: taxYear ? { taxYear } : undefined,
  });
  return res.data;
}

// SA103 Boxes
export async function getUkTaxBoxes(taxYear?: string): Promise<UkTaxBoxes> {
  const res = await apiClient.get<UkTaxBoxes>(ENDPOINTS.taxes.boxes, {
    params: taxYear ? { taxYear } : undefined,
  });
  return res.data;
}

// VAT
export async function getVatReturn(periodStart: string, periodEnd: string): Promise<VatReturnDraft> {
  const res = await apiClient.get<VatReturnDraft>(ENDPOINTS.taxes.vatReturn, {
    params: { periodStart, periodEnd },
  });
  return res.data;
}

// MTD
export async function getMtdStatus(): Promise<MtdStatus> {
  const res = await apiClient.get<MtdStatus>(ENDPOINTS.taxes.mtdStatus);
  return res.data;
}

// CT600
export async function getCt600(taxYear?: string): Promise<Ct600Summary> {
  const res = await apiClient.get<Ct600Summary>(ENDPOINTS.taxes.ct600, {
    params: taxYear ? { taxYear } : undefined,
  });
  return res.data;
}

// CIS
export async function getCisSubcontractors(): Promise<CisSubcontractor[]> {
  const res = await apiClient.get<CisSubcontractor[]>(ENDPOINTS.taxes.cisSubcontractors);
  return res.data;
}

export async function createCisSubcontractor(input: CisSubcontractorCreateInput): Promise<CisSubcontractor> {
  const res = await apiClient.post<CisSubcontractor>(ENDPOINTS.taxes.cisSubcontractors, input);
  return res.data;
}

export async function deleteCisSubcontractor(id: string): Promise<{ deleted: boolean }> {
  const res = await apiClient.delete<{ deleted: boolean }>(ENDPOINTS.taxes.cisSubcontractor(id));
  return res.data;
}

export async function getCis300(periodStart: string, periodEnd: string): Promise<Cis300Draft> {
  const res = await apiClient.get<Cis300Draft>(ENDPOINTS.taxes.cis300, {
    params: { periodStart, periodEnd },
  });
  return res.data;
}

// Deadlines
export async function getUkDeadlines(): Promise<UkTaxDeadline[]> {
  const res = await apiClient.get<UkTaxDeadline[]>(ENDPOINTS.taxes.deadlines);
  return res.data;
}

// Filing drafts
export async function getUkFilingDrafts(): Promise<TaxFilingDraftSummary[]> {
  const res = await apiClient.get<TaxFilingDraftSummary[]>(ENDPOINTS.taxes.filingDrafts);
  return res.data;
}
